<template>
  <section class="field-selection min-w-0 pb-10">
    <span class="text-black font-semibold flex items-center">
      FIELD SELECTION
    </span>
    <p class="text-gray-dark text-sm mt-1">
      Choose how fields from the first <code class="text-black">&lt;{{ itemTag || 'item' }}&gt;</code> item are used.
    </p>
    <p class="text-gray-dark text-sm mt-1">
      {{ payloadFieldCount }} Output · {{ embedFieldCount }} Search · {{ groupKeyCount }} Group key
    </p>

    <label class="field-filter mt-3" aria-label="Filter fields">
      <vs-icon icon="search" class="field-filter__icon text-gray"/>
      <input v-model.trim="filter"
             class="field-filter__input"
             type="search"
             placeholder="Filter fields"
      />
    </label>

    <div class="field-table mt-3" role="table" aria-label="XML feed field selection">
      <div class="field-table__header" role="row">
        <span role="columnheader" class="field-table__field-heading">Field &amp; example</span>
        <span role="columnheader" class="field-table__input-heading"></span>
        <span role="columnheader" class="field-table__role-heading">
          Output
          <vs-tooltip class="field-table__hint" position="left">
            <template #content>
              <div v-html="outputHint"/>
            </template>
            <vs-icon icon="info" class="field-table__info-icon text-gray cursor-default"/>
          </vs-tooltip>
        </span>
        <span role="columnheader" class="field-table__role-heading">
          Search
          <vs-tooltip class="field-table__hint" position="left">
            <template #content>
              <div v-html="searchHint"/>
            </template>
            <vs-icon icon="info" class="field-table__info-icon text-gray cursor-default"/>
          </vs-tooltip>
        </span>
        <span role="columnheader" class="field-table__role-heading">
          Group
          <vs-tooltip class="field-table__hint" position="left">
            <template #content>
              <div v-html="groupKeyHint"/>
            </template>
            <vs-icon icon="info" class="field-table__info-icon text-gray cursor-default"/>
          </vs-tooltip>
        </span>
        <span class="field-table__line" aria-hidden="true"/>
      </div>

      <div class="field-table__body">
        <div v-for="tag in filteredTags" :key="tag" class="field-table__row" role="row">
          <div class="field-table__field" role="cell">
            <span class="field-table__key font-mono text-sm" :title="tag">{{ tag }}</span>
          </div>

          <div class="field-table__alias" role="cell">
            <template v-if="isPayload(tag)">
              <vs-icon icon="arrow_forward" class="field-table__alias-arrow text-gray"/>
              <input :value="payloadFields[tag].as"
                     :aria-label="`Save ${tag} as`"
                     data-testid="payload-alias"
                     class="field-table__alias-input"
                     type="text"
                     @input="setPayloadProp(tag, 'as', $event.target.value)"
              />
            </template>
          </div>

          <label class="field-table__checkbox" :aria-label="`Save ${tag} as an alias`" role="cell">
            <input type="checkbox"
                   :checked="isPayload(tag)"
                   @change="togglePayload(tag, $event.target.checked)"
            />
          </label>

          <div class="field-table__checkbox" role="cell">
            <label :aria-label="`Use ${tag} for search`" class="field-table__checkbox-hit">
              <input type="checkbox"
                     :checked="isEmbed(tag)"
                     @change="toggleEmbed(tag, $event.target.checked)"
              />
            </label>
            <vs-tooltip v-if="showNumericSearchWarning(tag)"
                        class="field-table__search-warning cursor-default ml-1"
                        data-testid="numeric-search-warning"
                        position="left"
            >
              <template #content>
                <div v-html="numericSearchWarningReason(tag)"/>
              </template>
              ⚠️
            </vs-tooltip>
          </div>

          <label class="field-table__checkbox" :aria-label="`Use ${tag} as group key`" role="cell">
            <input type="checkbox"
                   :checked="isGroupKey(tag)"
                   @change="toggleGroupKey(tag, $event.target.checked)"
            />
          </label>

          <span v-if="fieldExample(tag)"
                class="field-table__example text-gray text-xs truncate"
                role="cell"
                :title="fieldExample(tag)"
          >{{ fieldExample(tag) }}</span>
          <span class="field-table__line" aria-hidden="true"/>
        </div>

        <p v-if="availableTags.length && !filteredTags.length" class="field-table__empty text-gray text-sm py-4 text-center">
          No matching fields
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import equals from 'ramda/src/equals'
import without from 'ramda/src/without'
import UserProfileService from '@/services/amio/user-profile.service'

// e.g. 'g:item_group_id' → 'item_group_id', 'g:price' → 'price', 'foo-bar' → 'foo_bar'
function defaultPayloadName(tag) {
  return tag.replace(/^[a-zA-Z]+:/, '').replace(/[^a-zA-Z0-9_]/g, '_')
}

const SEARCH_RECOMMENDED_FIELDS = new Set(['title', 'description', 'material'])
const OUTPUT_ONLY_FIELDS = new Set(['price', 'sale_price', 'availability'])
const COMMON_SEARCH_FIELDS = new Set(['gender', 'size_type', 'size_system', 'age_group'])
const STOCK_FIELDS = new Set(['stock', 'stock_lvl', 'stock_level'])

function normalizedTag(tag) {
  return defaultPayloadName(tag).toLowerCase()
}

function isExcludedTag(tag) {
  return normalizedTag(tag) === 'stock_lvl'
}

function sanitizeSelection(value = {}) {
  return {
    embed_fields: (value.embed_fields || []).filter(tag => !isExcludedTag(tag)),
    payload_fields: Object.entries(value.payload_fields || {}).reduce((fields, [tag, config]) => {
      if (!isExcludedTag(tag)) fields[tag] = config
      return fields
    }, {}),
    group_key: (value.group_key || []).filter(tag => !isExcludedTag(tag))
  }
}

export default {
  name: 'SettingsIndexFieldSelection',
  props: {
    availableTags: {
      type: Array,
      default: () => []
    },
    fieldExamples: {
      type: Object,
      default: () => ({})
    },
    itemTag: {
      type: String,
      default: ''
    },
    value: {
      type: Object,
      default: () => ({})
    },
    recommendDefaults: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const value = sanitizeSelection(this.value)
    return {
      filter: '',
      embedFields: [...value.embed_fields],
      payloadFields: { ...value.payload_fields },
      groupKey: [...value.group_key],
      hasRecommendedDefaults: false
    }
  },
  computed: {
    searchHint() {
      return `AI agent will search for the products using this field.
        <br/><br/>
        Recommended: title, description, material, and genuinely product-specific metadata.
        <br/><br/>
        If a value is shared across many products, it is not a good Search candidate e.g. gender.
        If it is only a number, it is not a good Search candidate.`
    },
    groupKeyHint() {
      return `Chatbot uses it to group products by category.
        <br/><br/>
        Group variants of the same product so they can share one search embedding. Use a stable parent-product ID.`
    },
    outputHint() {
      const outputHint = `Return this field for filtering or product-card display. You can change the name used in the returned data.
        <br/><br/>
        Use <strong>"price"</strong> for the standard price, <strong>"sale_price"</strong> for the discounted price, and <strong>"availability"</strong> for the normalized in-stock flag.`
      if (UserProfileService.isAdmin() || this.showUnreleased) {
        return outputHint + '<br/><br/>' + 'Which payload fields are indexed is set per collection in Settings → Vector Collections.'
      }
      return outputHint
    },
    aliasHint() {
      return this.outputHint
    },
    selectableTags() {
      return this.availableTags.filter(tag => !isExcludedTag(tag))
    },
    filteredTags() {
      const query = this.filter.toLowerCase()
      return [...this.selectableTags]
        .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
        .filter(tag => !query ||
          tag.toLowerCase().includes(query) ||
          this.fieldExample(tag).toLowerCase().includes(query))
    },
    payloadFieldCount() {
      return this.selectableTags.filter(tag => this.isPayload(tag)).length
    },
    embedFieldCount() {
      return this.selectableTags.filter(tag => this.isEmbed(tag)).length
    },
    groupKeyCount() {
      return this.selectableTags.filter(tag => this.isGroupKey(tag)).length
    },
    config() {
      return {
        embed_fields: this.embedFields,
        payload_fields: this.payloadFields,
        group_key: this.groupKey
      }
    }
  },
  watch: {
    availableTags: {
      immediate: true,
      handler(tags) {
        this.applyRecommendedDefaults(tags)
      }
    },
    value: {
      deep: true,
      handler(value) {
        const sanitizedValue = sanitizeSelection(value)
        const nextConfig = {
          embed_fields: [...sanitizedValue.embed_fields],
          payload_fields: { ...sanitizedValue.payload_fields },
          group_key: [...sanitizedValue.group_key]
        }
        if (equals(nextConfig, this.config)) return

        this.embedFields = nextConfig.embed_fields
        this.payloadFields = nextConfig.payload_fields
        this.groupKey = nextConfig.group_key
      }
    },
    config: {
      deep: true,
      handler(config) {
        this.$emit('input', config)
      }
    }
  },
  methods: {
    applyRecommendedDefaults(tags) {
      if (!this.recommendDefaults || this.hasRecommendedDefaults || !tags.length) return
      this.hasRecommendedDefaults = true
      if (this.embedFields.length || Object.keys(this.payloadFields).length || this.groupKey.length) return

      this.embedFields = tags.filter(tag =>
        SEARCH_RECOMMENDED_FIELDS.has(normalizedTag(tag)) && !this.isNumericSearchField(tag))
      this.payloadFields = tags.reduce((fields, tag) => {
        if (OUTPUT_ONLY_FIELDS.has(normalizedTag(tag))) {
          fields[tag] = { as: defaultPayloadName(tag) }
        }
        return fields
      }, {})
    },
    fieldExample(tag) {
      const value = this.fieldExamples[tag]
      if (value === null || value === undefined) return ''
      return typeof value === 'object' ? JSON.stringify(value) : String(value)
    },
    isEmbed(tag) {
      return this.embedFields.includes(tag)
    },
    isPayload(tag) {
      return Object.prototype.hasOwnProperty.call(this.payloadFields, tag)
    },
    isGroupKey(tag) {
      return this.groupKey.includes(tag)
    },
    isNumericSearchField(tag) {
      return /^-?\d+(?:[.,]\d+)?(?:-\d+(?:[.,]\d+)?)*$/.test(this.fieldExample(tag).trim())
    },
    showNumericSearchWarning(tag) {
      return this.isEmbed(tag) && (
        this.isNumericSearchField(tag) || OUTPUT_ONLY_FIELDS.has(normalizedTag(tag)) || STOCK_FIELDS.has(normalizedTag(tag)) || COMMON_SEARCH_FIELDS.has(normalizedTag(tag))
      )
    },
    numericSearchWarningReason(tag) {
      const normalized = normalizedTag(tag)
      if (OUTPUT_ONLY_FIELDS.has(normalized) || STOCK_FIELDS.has(normalized) || normalized === 'product_id' || normalized === 'id') {
        return `Price, availability, and stock fields are not useful for semantic search. Including them may worsen the search results.
          <br/><br/>
          An exception is a numeric product ID, which can be useful to include if your customers search by it — but only when the Chat GPT "Query type" is set to "Hybrid."`
      }
      if (COMMON_SEARCH_FIELDS.has(normalized)) {
        return 'This value is shared across many products, so it adds noise instead of distinguishing this product.'
      }
      return 'This value is only a number, so it adds no useful semantic meaning. It is not recommended as a Search field.'
    },
    toggleEmbed(tag, on) {
      this.embedFields = on ? [...new Set([...this.embedFields, tag])] : without([tag], this.embedFields)
    },
    toggleGroupKey(tag, on) {
      this.groupKey = on ? [...this.groupKey, tag] : without([tag], this.groupKey)
    },
    togglePayload(tag, on) {
      const payloadFields = { ...this.payloadFields }
      if (on) {
        payloadFields[tag] = { as: defaultPayloadName(tag) }
      } else {
        delete payloadFields[tag]
      }
      this.payloadFields = payloadFields
    },
    setPayloadProp(tag, prop, value) {
      this.payloadFields = {
        ...this.payloadFields,
        [tag]: { ...this.payloadFields[tag], [prop]: value }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.field-filter {
  align-items: center;
  border: 1px solid #DCE1EA;
  border-radius: 4px;
  display: flex;
  height: 36px;
  max-width: 220px;
  padding: 0 10px;

  &:focus-within {
    border-color: #7E50FF;
  }
}

.field-filter__icon {
  font-size: 18px;
  margin-right: 7px;
}

.field-filter__input {
  background: transparent;
  border: 0;
  color: inherit;
  min-width: 0;
  outline: 0;
  width: 100%;
}

.field-table {
  --field-table-columns: minmax(0, 1fr) calc(8rem + 10px) 3.75rem 3.75rem 3.75rem;
  font-size: 12px;
  min-width: 0;
  width: 100%;
}

.field-table__header,
.field-table__body {
  column-gap: 8px;
  display: grid;
  grid-template-columns: var(--field-table-columns);
}

.field-table__header {
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 2;
}

.field-table__row {
  display: contents;
}

.field-table__field-heading,
.field-table__input-heading,
.field-table__role-heading {
  align-items: center;
  color: #66748E;
  display: flex;
  font-weight: 500;
  min-height: 32px;
}

.field-table__field-heading {
  white-space: nowrap;
}

.field-table__role-heading {
  font-size: 11px;
  gap: 2px;
  justify-content: center;
  line-height: 1;
  white-space: nowrap;
}

.field-table__hint {
  display: inline-flex;
  flex-shrink: 0;
}

.field-table__info-icon {
  font-size: 14px;
}

.field-table__line {
  border-bottom: 1px solid #DCE1EA;
  grid-column: 1 / -1;
  height: 0;
  margin: 4px 0;
}

.field-table__empty {
  grid-column: 1 / -1;
}

.field-table__field {
  min-width: 0;
}

.field-table__key {
  align-items: center;
  display: block;
  height: 25px;
  line-height: 25px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-table__example {
  grid-column: 1 / 3;
  margin-top: 2px;
  min-width: 0;
}

.field-table__checkbox {
  align-items: center;
  display: flex;
  height: 25px;
  justify-content: center;
  justify-self: stretch;
  position: relative;
  width: 100%;
}

.field-table__search-warning {
  display: inline-flex;
  left: calc(50% + 10px);
  position: absolute;
}

.field-table__warning-icon {
  font-size: 14px;
}

.field-table__checkbox-hit {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.field-table__alias {
  align-items: center;
  display: flex;
  gap: 4px;
  height: 25px;
}

.field-table__alias-arrow {
  flex-shrink: 0;
  font-size: 14px;
}

.field-table__alias-input {
  background: white;
  border: 1px solid #DCE1EA;
  border-radius: 3px;
  flex: 1;
  font-size: 11px;
  height: 25px;
  min-width: 0;
  padding: 0 6px;
  width: 100%;

  &:focus {
    border-color: #7E50FF;
    outline: 0;
  }
}
</style>
